import React, { useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import {
  SEARCH_PIPELINES_QUERY,
  transformPipelineSearchResponse,
} from '../../utils/model_server';
import { modelsIndexPollInterval } from '../../config';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

const PipelineSearchContext = React.createContext({
  error: null,
  loading: false,
  pipelines: [],
  searchQuery: '',
  setSearchQuery: () => { },
});

const usePipelineSearchQuery = (query = '', workspaceId = '') => {
  const [searchQuery, setSearchQuery] = useState(query);

  const wildcardAugmentedSearchQuery = `%${searchQuery.toLowerCase()}%`;

  const searchQueryResult = useQuery(SEARCH_PIPELINES_QUERY, {
    skip: !workspaceId,
    variables: {
      query: wildcardAugmentedSearchQuery,
      workspace_id: workspaceId,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelsIndexPollInterval,
  });

  if (searchQueryResult.error) {
    console.error('got error', searchQueryResult.error);
  }

  const pipelines = searchQueryResult?.data?.search_workspace_pipelines && !searchQueryResult?.error
    ? searchQueryResult.data.search_workspace_pipelines.map(transformPipelineSearchResponse)
    : null;

  return {
    searchQuery,
    pipelines,
    setSearchQuery,
    loading: searchQueryResult.loading,
    error: searchQueryResult.error,
  };
};

export const PipelineSearchProvider = ({ children, query = '' }) => {
  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    pipelines,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  } = usePipelineSearchQuery(query, currentWorkspaceId)

  const pipelineSearchContextValue = useMemo(() => ({
    pipelines,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  }), [
    pipelines,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  ])

  return (
    <PipelineSearchContext.Provider
      value={pipelineSearchContextValue}
    >
      {children}
    </PipelineSearchContext.Provider>
  )
}

export const usePipelineSearch = () => useContext(PipelineSearchContext);

export default usePipelineSearch;
